<template>
  <v-card>
    <v-container class="py-0">
      <v-row>
        <v-col cols="auto" class="pr-0">
          <user-avatar :size="32" :user="talkingPoint.createdBy"></user-avatar>
        </v-col>
        <v-col cols="auto" class="my-auto">
          <div class="secondarytext--text">
            {{ talkingPoint.createdBy.fullName }}
          </div>
          <div class="caption lighttext--text">
            Created {{ talkingPoint.createdDate | longDate }}
          </div>
        </v-col>
        <v-col cols="auto" class="px-0">
          <urgent-check-in-icon
            :talkingPoint="talkingPoint"
          ></urgent-check-in-icon>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto" class="px-0">
          <v-btn icon @click="$emit('cancel')" v-if="readOnly">
            <v-icon>close</v-icon>
          </v-btn>
          <v-menu
            absolute
            v-else-if="
              !isComplete && talkingPoint.canSave && !isCheckinComplete
            "
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon>
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="edit">
                <v-list-item-icon>
                  <v-icon>edit</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>
              <v-list-item @click="deleteTalkingPoint">
                <v-list-item-icon>
                  <v-icon>delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
      <v-row class="subtitle-1 secondarytext--text">
        <v-col cols="12" class="white-space-pre-line">
          {{ talkingPoint.text }}
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-btn
            small
            text
            color="lighttext"
            v-if="(talkingPoint.notes || []).length > 0"
            class="pa-0 mr-2"
            @click="show_notes = !show_notes"
          >
            {{ (talkingPoint.notes || []).length }} Comments
            <v-icon small>
              {{ !show_notes ? "expand_more" : "expand_less" }}
            </v-icon>
          </v-btn>
          <v-btn
            small
            text
            color="lighttext"
            v-if="actionItems.length > 0"
            class="pa-0"
            @click="show_action_items = !show_action_items"
          >
            {{ actionItems.length }} Action Items
            <v-icon small>
              {{ !show_action_items ? "expand_more" : "expand_less" }}
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <template v-if="show_notes">
      <template v-for="note in talkingPoint.notes">
        <note-line-item
          class="mx-0"
          :key="note.id"
          :note="note"
          v-on:edit="editNote(note)"
          deleteAction="checkin/deleteTalkingPointNote"
          :disabled="isComplete || readOnly"
        >
        </note-line-item>
      </template>
    </template>
    <template v-if="show_action_items">
      <template v-for="actionItem in actionItems">
        <action-item-line-item
          class="mx-0"
          :key="actionItem.id"
          :actionItem="actionItem"
          v-on:edit="editActionItem(actionItem)"
          :allowCompletion="false"
          :disabled="isComplete || readOnly"
          :hideActions="isCheckinComplete"
          v-on:redraw="$emit('redraw')"
          small-text
        ></action-item-line-item>
      </template>
    </template>
    <v-card-actions
      class="background rounded-b-lg pa-1"
      v-if="!isCheckinComplete && !readOnly"
    >
      <v-menu absolute v-if="!isComplete">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            icon
            color="lighttext"
            class="text--darken-1"
            large
          >
            <v-icon>add_circle</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item @click="addNote">
            <v-list-item-icon>
              <v-badge color="noteprimary" dot overlap>
                <v-icon>message</v-icon>
              </v-badge>
            </v-list-item-icon>
            <v-list-item-title>Note</v-list-item-title>
          </v-list-item>
          <v-list-item @click="addActionItem">
            <v-list-item-icon>
              <v-badge color="info" dot overlap>
                <v-icon>check_box</v-icon>
              </v-badge>
            </v-list-item-icon>
            <v-list-item-title>Action Item</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-spacer></v-spacer>
      <template v-if="checkin.startDate != null">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="lighttext"
              icon
              @click="snooze"
              v-bind="attrs"
              v-on="on"
              v-if="!talkingPoint.snoozed"
            >
              <v-icon>restore</v-icon>
            </v-btn>
          </template>
          <div class="tooltip-limit-width">
            Snooze until next Meeting
          </div>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :color="isComplete ? 'performance' : 'lighttext'"
              icon
              @click="handleComplete"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>check</v-icon>
            </v-btn>
          </template>
          <div class="tooltip-limit-width">
            {{
              isComplete
                ? "Reopen Talking Point"
                : "Mark Talking Point Completed"
            }}
          </div>
        </v-tooltip>
      </template>
    </v-card-actions>
    <edit-talking-point-dialog
      ref="editTalkingPoint"
      v-on:save="$emit('redraw')"
    ></edit-talking-point-dialog>
    <edit-note-dialog
      ref="editNote"
      saveAction="checkin/saveTalkingPointNote"
      label="talking point"
      v-on:save="$emit('redraw')"
    ></edit-note-dialog>
    <edit-action-item-dialog
      ref="editActionItem"
      v-on:save="$emit('redraw')"
    ></edit-action-item-dialog>
  </v-card>
</template>

<script>
import UrgentCheckInIcon from "@/components/checkin/UrgentCheckInIcon";
import NoteLineItem from "@/components/checkin/NoteLineItem";
import ActionItemLineItem from "@/components/checkin/ActionItemLineItem";
import EditTalkingPointDialog from "@/components/checkin/EditTalkingPointDialog";
import EditNoteDialog from "@/components/checkin/EditNoteDialog";
import EditActionItemDialog from "@/components/checkin/EditActionItemDialog";
import { mapGetters } from "vuex";
export default {
  components: {
    EditTalkingPointDialog,
    EditNoteDialog,
    EditActionItemDialog,
    NoteLineItem,
    ActionItemLineItem,
    UrgentCheckInIcon
  },
  name: "TalkingPoint",
  props: {
    talkingPoint: { type: Object },
    readOnly: { type: Boolean, default: false }
  },
  data: () => ({ show_notes: false, show_action_items: false }),
  watch: {
    show_notes(val, oldVal) {
      if (val != oldVal) {
        this.$emit("redraw");
      }
    },
    show_action_items(val, oldVal) {
      if (val != oldVal) {
        this.$emit("redraw");
      }
    }
  },
  computed: {
    isComplete() {
      return this.talkingPoint.completeDate != null;
    },
    isCheckinComplete() {
      return this.checkin.completeDate != null;
    },
    actionItems() {
      let actionItems = [];
      if (!this._.isEmpty(this.checkin)) {
        actionItems = this.checkin.actionItems.filter(
          x => x.relatedTalkingPointId == this.talkingPoint.id
        );
      }

      return this._.orderBy(
        actionItems,
        ["completeDate", "dueDate"],
        ["desc", "asc"]
      );
    },
    ...mapGetters({ checkin: "checkin/checkin" })
  },
  methods: {
    edit() {
      this.$refs.editTalkingPoint.showDialog(this.talkingPoint);
    },
    addNote() {
      this.$refs.editNote.showDialog({
        parentId: this.talkingPoint.id,
        text: ""
      });
    },
    editNote(note) {
      this.$refs.editNote.showDialog(note);
    },
    addActionItem() {
      this.$refs.editActionItem.showDialog({
        relatedTalkingPointId: this.talkingPoint.id
      });
    },
    editActionItem(actionItem) {
      this.$refs.editActionItem.showDialog(actionItem);
    },
    async handleComplete() {
      if (this.isComplete) {
        this.uncomplete();
      } else {
        this.complete();
      }
      this.$emit("redraw");
    },
    async complete() {
      this.$store
        .dispatch("checkin/completeTalkingPoint", this.talkingPoint.id)
        .then(() => {
          this.$emit("save");
          this.$root.$snackbarSuccess("Talking Point discussed");
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occurred while saving your talking point."
          );
        });
    },
    uncomplete() {
      this.$store
        .dispatch("checkin/unCompleteTalkingPoint", this.talkingPoint.id)
        .then(() => {
          this.$emit("save");
          this.$root.$snackbarSuccess("Talking Point reopened");
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occurred while saving your talking point."
          );
        });
    },
    async snooze() {
      if (
        await this.$root.$confirm(
          "Snooze talking point",
          "Confirm that you’d like to snooze this talking point until your next meeting. Action items will still appear in the action items area.",
          { color: "performance" }
        )
      ) {
        this.$store
          .dispatch("checkin/snoozeTalkingPoint", this.talkingPoint.id)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Talking Point snoozed");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while saving your talking point."
            );
          });
      }
    },
    async deleteTalkingPoint() {
      if (
        await this.$root.$confirm(
          "Delete talking point",
          "Are you sure you want to delete this talking point?",
          { color: "red" }
        )
      ) {
        this.saving = true;
        this.$store
          .dispatch("checkin/deleteTalkingPoint", this.talkingPoint.id)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Talking Point deleted");
            this.dialog = false;
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while deleting a talking point."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.v-card {
  border-top: solid 8px var(--v-performance-base);
}
</style>
